import React from 'react';
import { Label, TextInput } from 'flowbite-react';
import { PropTypes } from 'prop-types';
import { CONFIDENCE_SCORE } from 'configs/confidence';

const getInputType = fieldType => {
  switch (fieldType) {
    case 'date':
      return 'date';
    case 'integer':
    case 'number':
    case 'currency':
      return 'number';
    default:
      return 'text';
  }
};

const normalizeValueForInput = value => {
  if (value === null || value === undefined) {
    return '';
  }

  return value;
};

/** API often sends thousands separators; strip for type="number" display. */
const normalizeNumericInputValue = (value, fieldType) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (fieldType === 'integer' || fieldType === 'number' || fieldType === 'currency') {
    return String(value).replace(/,/g, '');
  }
  return normalizeValueForInput(value);
};

const AppraisalFields = ({
  values,
  formik,
  isVerified,
  sections: visibleSections,
  sectionOffset = 0,
}) => {
  const sections = visibleSections || values?.sections || [];

  return (
    <div className="px-5 py-4" data-testid="test-appraisal-field-editor">
      {sections.map((section, sectionIndex) => {
        const actualSectionIndex =
          section._arrayIndex !== undefined
            ? section._arrayIndex
            : sectionOffset + sectionIndex;

        return (
        <div key={section.id || sectionIndex} className="mb-6">
          <div className="mb-3 text-base font-semibold text-gray-900">{section.label}</div>
          {(section.groups || []).map((group, displayGroupIndex) => {
            const formikGroupIndex =
              group._formikGroupIndex !== undefined
                ? group._formikGroupIndex
                : displayGroupIndex;

            return (
            <div
              key={group.id || `${section.id}-${displayGroupIndex}`}
              className="mb-4 rounded-lg border border-gray-200 p-3"
            >
              <div className="mb-3 text-sm font-medium text-gray-700">{group.label}</div>
              <div className="space-y-3">
                {(group.fields || []).map((field, displayFieldIndex) => {
                  const formikFieldIndex =
                    field._formikFieldIndex !== undefined
                      ? field._formikFieldIndex
                      : displayFieldIndex;
                  const name = `sections[${actualSectionIndex}].groups[${formikGroupIndex}].fields[${formikFieldIndex}].value`;
                  const fieldValue = formik.getFieldProps(name).value;
                  const hasLowConfidence =
                    typeof field.confidence === 'number' &&
                    field.confidence < CONFIDENCE_SCORE;

                  if (field.type === 'boolean') {
                    return (
                      <label
                        key={field.id || name}
                        className="flex items-center gap-2 text-sm text-gray-700"
                      >
                        <input
                          type="checkbox"
                          checked={!!fieldValue}
                          disabled={isVerified || field.readOnly}
                          onChange={event => {
                            formik.setFieldValue(name, event.target.checked);
                          }}
                        />
                        {hasLowConfidence && (
                          <span className="rounded-full bg-red-500 w-1.5 h-1.5" />
                        )}
                        <span>{field.label}</span>
                      </label>
                    );
                  }

                  if (field.type === 'enum') {
                    return (
                      <div key={field.id || name}>
                        <div className="mb-1 flex items-center gap-1">
                          {hasLowConfidence && (
                            <span className="rounded-full bg-red-500 w-1.5 h-1.5" />
                          )}
                          <Label htmlFor={field.id || name} value={field.label} />
                        </div>
                        <select
                          id={field.id || name}
                          name={name}
                          className="block w-full rounded-lg border border-gray-300 p-2.5 text-sm text-gray-900"
                          disabled={isVerified || field.readOnly}
                          value={normalizeValueForInput(fieldValue)}
                          onChange={formik.handleChange}
                        >
                          <option value="">Select an option</option>
                          {(field.options || []).map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field.type === 'textarea') {
                    return (
                      <div key={field.id || name}>
                        <div className="mb-1 flex items-center gap-1">
                          {hasLowConfidence && (
                            <span className="rounded-full bg-red-500 w-1.5 h-1.5" />
                          )}
                          <Label htmlFor={field.id || name} value={field.label} />
                        </div>
                        <textarea
                          id={field.id || name}
                          name={name}
                          className="min-h-24 w-full rounded-lg border border-gray-300 p-3 text-sm text-gray-900"
                          disabled={isVerified || field.readOnly}
                          value={normalizeValueForInput(fieldValue)}
                          onChange={formik.handleChange}
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field.id || name}>
                      <div className="mb-1 flex items-center gap-1">
                        {hasLowConfidence && (
                          <span className="rounded-full bg-red-500 w-1.5 h-1.5" />
                        )}
                        <Label htmlFor={field.id || name} value={field.label} />
                      </div>
                      <TextInput
                        id={field.id || name}
                        name={name}
                        type={getInputType(field.type)}
                        disabled={isVerified || field.readOnly}
                        value={normalizeNumericInputValue(fieldValue, field.type)}
                        onChange={event => {
                          const nextValue = event.target.value;

                          if (
                            field.type === 'integer' ||
                            field.type === 'number' ||
                            field.type === 'currency'
                          ) {
                            const cleaned = nextValue.replace(/,/g, '');
                            formik.setFieldValue(name, cleaned);
                            return;
                          }

                          formik.handleChange(event);
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })}
        </div>
        );
      })}
    </div>
  );
};

AppraisalFields.propTypes = {
  values: PropTypes.object,
  formik: PropTypes.object.isRequired,
  isVerified: PropTypes.bool,
  sections: PropTypes.array,
  sectionOffset: PropTypes.number,
};

export default AppraisalFields;
